"use client";

import { useMemo } from "react";
import type { CanvasVariant, VariantAxis } from "../canvas/variant-resolver";
import { ART_SETS, sellableSet, type ArtSet, type SetSize } from "./art-sets";
import type { Artwork } from "./artwork-types";
import { useArtworks, useCanvasVariants } from "./useArtwork";

/**
 * Everything a set needs from the catalogue, from the same cached reads the
 * gallery and the artwork page make — so arriving at a set from either costs
 * no request.
 *
 * Both storefronts are read because a set may hold photographs as well as
 * paintings.
 */
export function useSetCatalogue() {
  const art = useArtworks("ART");
  const photo = useArtworks("PHOTO");
  const { data: variantRows, isError: variantsFailed, isLoading: variantsLoading } =
    useCanvasVariants();

  const catalogue = useMemo<Artwork[]>(
    () => [...(art.data ?? []), ...(photo.data ?? [])],
    [art.data, photo.data],
  );
  const variants = useMemo<CanvasVariant[]>(() => variantRows ?? [], [variantRows]);
  const axes = useMemo<VariantAxis[]>(
    () =>
      ((variantRows?.[0] as { definition?: { variantAxes?: VariantAxis[] } } | undefined)
        ?.definition?.variantAxes ?? []) as VariantAxis[],
    [variantRows],
  );

  return {
    catalogue,
    variants,
    axes,
    isLoading: art.isLoading || photo.isLoading || variantsLoading,
    isError: (art.isError && photo.isError) || variantsFailed,
  };
}

export interface SellableSet {
  set: ArtSet;
  pieces: Artwork[];
  sizes: SetSize[];
}

/**
 * The sets that can be bought right now, in list order — all of them, or only
 * those holding `artworkSlug`.
 */
export function useSellableSets(artworkSlug?: string) {
  const { catalogue, variants, isLoading } = useSetCatalogue();
  const sets = useMemo<SellableSet[]>(() => {
    if (!catalogue.length || !variants.length) return [];
    return ART_SETS.filter((s) => !artworkSlug || s.artworkSlugs.includes(artworkSlug)).flatMap((set) => {
      const sellable = sellableSet(set, catalogue, variants);
      return sellable ? [{ set, ...sellable }] : [];
    });
  }, [catalogue, variants, artworkSlug]);
  return { sets, isLoading };
}
